/* predictive-search.js ─ typeahead suggestions for search inputs ───────────

   Hooks every input matching [data-predictive-search] and renders a panel
   of live suggestions below it. Uses Shopify's /search/suggest.json which
   - unlike the standard storefront search - matches variant titles, so a
   user typing a colorway like "Snowflake" sees the parent product.

   Markup contract:
     <input data-predictive-search data-target-panel="#some-id">
     <div id="some-id" class="predictive-search"></div>

   The script writes:
     <div class="predictive-search__inner">
       <section class="predictive-search__group">
         <p class="predictive-search__heading">Garne</p>
         <ul class="predictive-search__list">…</ul>
       </section>
       …
     </div>
*/
(function () {
  'use strict';

  const DEBOUNCE  = 200;
  const MIN_QUERY = 2;
  const PROD_LIM  = 6;
  const ART_LIM   = 4;
  const PAGE_LIM  = 3;

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatMoney(value) {
    // /search/suggest.json returns `price` as a string in major units (e.g. "8.90"),
    // unlike most Shopify JSON APIs which return cents. Parse as float, no /100.
    if (value == null || value === '') return '';
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!isFinite(n)) return '';
    const euros = n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${euros} €`;
  }

  function highlightMatch(text, query) {
    const t = String(text || '');
    const q = String(query || '').trim();
    if (!q) return escapeHtml(t);
    const idx = t.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return escapeHtml(t);
    return (
      escapeHtml(t.slice(0, idx)) +
      '<mark>' + escapeHtml(t.slice(idx, idx + q.length)) + '</mark>' +
      escapeHtml(t.slice(idx + q.length))
    );
  }

  function renderProducts(products, q) {
    if (!products.length) return '';
    const items = products.slice(0, PROD_LIM).map(p => {
      const img = p.featured_image && p.featured_image.url
        ? `<img src="${escapeHtml(p.featured_image.url.replace(/(\.[a-z]+)(\?.*)?$/i, '_80x$1$2'))}" alt="" loading="lazy">`
        : '<span class="predictive-search__img-placeholder"></span>';
      const price = p.price != null ? formatMoney(p.price) : '';
      const vendor = p.vendor ? `<span class="predictive-search__vendor">${escapeHtml(p.vendor)}</span>` : '';
      // If the matched substring is in the title, highlight it. Otherwise highlight in vendor.
      const titleHtml = highlightMatch(p.title, q);
      return `
        <li class="predictive-search__item">
          <a class="predictive-search__link" href="${escapeHtml(p.url)}">
            <span class="predictive-search__thumb">${img}</span>
            <span class="predictive-search__body">
              ${vendor}
              <span class="predictive-search__title">${titleHtml}</span>
              ${price ? `<span class="predictive-search__price t-mono">${price}</span>` : ''}
            </span>
          </a>
        </li>`;
    }).join('');
    return `
      <section class="predictive-search__group">
        <p class="predictive-search__heading t-mono">${bfT('ps_yarns', 'Garne')}</p>
        <ul class="predictive-search__list">${items}</ul>
      </section>`;
  }

  function renderArticles(articles, q) {
    if (!articles.length) return '';
    const items = articles.slice(0, ART_LIM).map(a => `
      <li class="predictive-search__item">
        <a class="predictive-search__link predictive-search__link--text" href="${escapeHtml(a.url)}">
          <span class="predictive-search__type t-mono">${bfT('ps_article', 'Artikel')}</span>
          <span class="predictive-search__title">${highlightMatch(a.title, q)}</span>
          ${a.summary_html ? `<span class="predictive-search__excerpt">${escapeHtml(String(a.summary_html).replace(/<[^>]+>/g, '').slice(0, 120))}</span>` : ''}
        </a>
      </li>`).join('');
    return `
      <section class="predictive-search__group">
        <p class="predictive-search__heading t-mono">${bfT('ps_library', 'Bibliothek')}</p>
        <ul class="predictive-search__list">${items}</ul>
      </section>`;
  }

  function renderPages(pages, q) {
    if (!pages.length) return '';
    const items = pages.slice(0, PAGE_LIM).map(p => `
      <li class="predictive-search__item">
        <a class="predictive-search__link predictive-search__link--text" href="${escapeHtml(p.url)}">
          <span class="predictive-search__type t-mono">${bfT('ps_page', 'Seite')}</span>
          <span class="predictive-search__title">${highlightMatch(p.title, q)}</span>
        </a>
      </li>`).join('');
    return `
      <section class="predictive-search__group">
        <p class="predictive-search__heading t-mono">${bfT('ps_pages', 'Seiten')}</p>
        <ul class="predictive-search__list">${items}</ul>
      </section>`;
  }

  function renderViewAll(q, total) {
    const url = `/search?q=${encodeURIComponent(q)}&type=product,article,page&options[prefix]=last`;
    return `
      <a class="predictive-search__view-all t-mono" href="${escapeHtml(url)}">
        ${bfT('ps_view_all_results_html', 'Alle {total} Ergebnisse für „{q}“ ansehen →').replace('{total}', total).replace('{q}', escapeHtml(q))}
      </a>`;
  }

  function renderEmptyWithFallback(q) {
    // Suggest came back empty - but Shopify's full storefront search uses
    // broader matching (substring/fulltext) and may still find results.
    // Always offer the user a path through instead of a dead-end.
    const url = `/search?q=${encodeURIComponent(q)}&type=product,article,page&options[prefix]=last`;
    return `
      <div class="predictive-search__empty-wrap">
        <p class="predictive-search__empty">${bfT('ps_no_quick', 'Keine Schnell-Treffer für „{q}“.').replace('{q}', escapeHtml(q))}</p>
        <a class="predictive-search__view-all t-mono" href="${escapeHtml(url)}">
          ${bfT('ps_view_all_html', 'Alle Ergebnisse für „{q}“ ansehen →').replace('{q}', escapeHtml(q))}
        </a>
      </div>`;
  }

  function fetchSuggestions(q) {
    const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article,page&resources[limit]=10&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag`;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
  }

  function totalHits(data) {
    if (!data || !data.resources || !data.resources.results) return 0;
    const r = data.resources.results;
    return (r.products || []).length + (r.articles || []).length + (r.pages || []).length;
  }

  // German compound words (Sockenwolle, Strickjacke, ...) often live as
  // single tokens in product titles, so a multi-word query like "socken
  // wolle" misses on prefix-matching. If the spaced form returns 0, retry
  // with all whitespace stripped before falling back to "view all".
  async function fetchWithCompoundFallback(q) {
    const primary = await fetchSuggestions(q);
    if (totalHits(primary) > 0) return primary;
    const collapsed = q.replace(/\s+/g, '');
    if (collapsed && collapsed !== q) {
      const secondary = await fetchSuggestions(collapsed);
      if (totalHits(secondary) > 0) return secondary;
    }
    return primary;
  }

  function renderResults(panel, data, q) {
    if (totalHits(data) === 0) {
      panel.innerHTML = `<div class="predictive-search__inner">${renderEmptyWithFallback(q)}</div>`;
      return;
    }
    const r = data.resources.results;
    const products = r.products || [];
    const articles = r.articles || [];
    const pages    = r.pages    || [];
    const total    = products.length + articles.length + pages.length;

    panel.innerHTML = `
      <div class="predictive-search__inner">
        ${renderProducts(products, q)}
        ${renderArticles(articles, q)}
        ${renderPages(pages, q)}
        ${renderViewAll(q, total)}
      </div>`;
  }

  function attach(input) {
    const targetSel = input.dataset.targetPanel;
    const panel = targetSel ? document.querySelector(targetSel) : null;
    if (!panel) return;

    // Server-rendered empty state (suggestion chips). Cache it so we can
    // restore it whenever the input goes back to empty after a search.
    const emptyStateHtml = panel.innerHTML;
    const hasEmptyState = !!panel.querySelector('[data-search-empty-state]');

    let timer = 0;
    let lastQuery = '';

    function showEmptyState() {
      if (!hasEmptyState) {
        panel.innerHTML = '';
        panel.dataset.open = '0';
        return;
      }
      panel.innerHTML = emptyStateHtml;
      panel.dataset.open = '1';
    }
    function close() {
      panel.innerHTML = hasEmptyState ? emptyStateHtml : '';
      panel.dataset.open = '0';
    }
    function open() {
      panel.dataset.open = '1';
    }

    // Show suggestions when the empty input gains focus.
    input.addEventListener('focus', () => {
      if (input.value.trim() === '') showEmptyState();
    });

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);
      lastQuery = '';
      if (q === '') {
        showEmptyState();
        return;
      }
      if (q.length < MIN_QUERY) {
        close();
        return;
      }
      lastQuery = q;
      timer = setTimeout(async () => {
        const data = await fetchWithCompoundFallback(q);
        renderResults(panel, data, q);
        open();
      }, DEBOUNCE);
    });

    // Click a suggestion chip → prefill input and trigger predictive search.
    panel.addEventListener('click', e => {
      const chip = e.target.closest('[data-search-prefill]');
      if (!chip) return;
      const term = chip.dataset.searchPrefill || chip.textContent.trim();
      if (!term) return;
      input.value = term;
      input.focus();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Close on outside click
    document.addEventListener('click', e => {
      if (e.target === input) return;
      if (panel.contains(e.target)) return;
      close();
    });

    // Close on Esc
    input.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        close();
        input.blur();
      }
    });
  }

  function init() {
    document.querySelectorAll('input[data-predictive-search]').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
